import React, { useState, useContext, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Modal, Badge } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import { 
    getCategorias, 
    getSubcategorias, 
    crearCategoria, 
    actualizarCategoria,
    toggleEstadoCategoria,
    crearSubcategoria,
    actualizarSubcategoria,
    toggleEstadoSubcategoria,
    getFondos
} from "../../api/gastos.api.ts";
import Swal from 'sweetalert2';

export default function GastosConfiguracionPage() {
    const { t } = useContext(TranslatorContext);
    
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState(''); // 'categoria' o 'subcategoria'
    const [selectedCategoria, setSelectedCategoria] = useState('');
    const [editingItem, setEditingItem] = useState(null);
    
    // Estados para la API
    const [categorias, setCategorias] = useState([]);
    const [subcategorias, setSubcategorias] = useState([]);
    const [fondos, setFondos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formData, setFormData] = useState({
        codigo: '',
        nombre: '',
        descripcion: '',
        color: '#000000',
        activo: true
    });

    // Cargar categorías desde la API
    const cargarCategorias = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getCategorias();
            if (response.success) {
                setCategorias(response.data);
            } else {
                setError('Error al cargar las categorías');
            }
        } catch (error) {
            setError('Error al conectar con el servidor');
            console.error('Error al cargar categorías:', error);
        } finally {
            setLoading(false);
        }
    };

    // Cargar subcategorías desde la API
    const cargarSubcategorias = async () => {
        try {
            const response = await getSubcategorias();
            if (response.success) {
                setSubcategorias(response.data);
            }
        } catch (error) {
            console.error('Error al cargar subcategorías:', error);
        }
    };

    // Cargar fondos desde la API
    const cargarFondos = async () => {
        try {
            const currentYear = new Date().getFullYear();
            const response = await getFondos(currentYear);
            if (response.success) {
                setFondos(response.data);
            }
        } catch (error) {
            console.error('Error al cargar fondos:', error);
        }
    };

    // Cargar datos al montar el componente
    useEffect(() => {
        cargarCategorias();
        cargarSubcategorias();
        cargarFondos();
    }, []);

    // Obtener subcategorías para una categoría específica
    const getSubcategoriasPorCategoria = (categoriaId) => {
        return subcategorias.filter(sub => sub.cat_id === categoriaId);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const abrirModal = (tipo, categoriaId = null, item = null) => {
        setModalType(tipo);
        setSelectedCategoria(categoriaId);
        setEditingItem(item);
        
        if (item) {
            // Modo edición
            setFormData({
                codigo: item.cat_codigo || item.sub_codigo || '',
                nombre: item.cat_nombre || item.sub_nombre || '',
                descripcion: item.cat_descripcion || item.sub_descripcion || '',
                color: item.cat_color || '#000000',
                activo: item.cat_activo !== undefined ? item.cat_activo : (item.sub_activo !== undefined ? item.sub_activo : true)
            });
        } else {
            // Modo creación
            setFormData({
                codigo: '',
                nombre: '',
                descripcion: '',
                color: '#000000',
                activo: true
            });
        }
        setShowModal(true);
    };

    const cerrarModal = () => {
        setShowModal(false);
        setModalType('');
        setSelectedCategoria('');
        setEditingItem(null);
        setFormData({
            codigo: '',
            nombre: '',
            descripcion: '',
            color: '#000000',
            activo: true
        });
    };

    const guardarConfiguracion = async () => {
        setLoading(true);
        try {
            let response;
            
            if (modalType === 'categoria') {
                const categoriaData = {
                    cat_codigo: formData.codigo,
                    cat_nombre: formData.nombre,
                    cat_descripcion: formData.descripcion,
                    cat_color: formData.color,
                    cat_activo: formData.activo
                };

                if (editingItem) {
                    response = await actualizarCategoria(editingItem.cat_id, categoriaData);
                } else {
                    response = await crearCategoria(categoriaData);
                }
            } else if (modalType === 'subcategoria') {
                const subcategoriaData = {
                    sub_categoria_id: selectedCategoria,
                    sub_codigo: formData.codigo,
                    sub_nombre: formData.nombre,
                    sub_descripcion: formData.descripcion,
                    sub_activo: formData.activo
                };

                if (editingItem) {
                    response = await actualizarSubcategoria(editingItem.sub_id, subcategoriaData);
                } else {
                    response = await crearSubcategoria(subcategoriaData);
                }
            }

            if (response.success) {
                Swal.fire({
                    title: 'Éxito!',
                    text: response.message || 'Operación realizada correctamente',
                    icon: 'success',
                    confirmButtonText: 'OK'
                });
                cargarCategorias();
                cargarSubcategorias();
                cerrarModal();
            } else {
                Swal.fire({
                    title: 'Error!',
                    text: response.message || 'Ocurrió un error',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Error!',
                text: 'Error al conectar con el servidor',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false);
        }
    };


    const toggleEstado = async (item, tipo) => {
        setLoading(true);
        try {
            let response;
            if (tipo === 'categoria') {
                response = await toggleEstadoCategoria(item.cat_id);
            } else {
                response = await toggleEstadoSubcategoria(item.sub_id);
            }
            
            if (response.success) {
                cargarCategorias();
                cargarSubcategorias();
            } else {
                Swal.fire('Error!', response.message, 'error');
            }
        } catch (error) {
            Swal.fire('Error!', 'Error al conectar con el servidor', 'error');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">Configuración de Gastos</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                                <li className="mc-breadcrumb-item"><Link to='/gastos' className="mc-breadcrumb-link">Gastos</Link></li>
                                <li className="mc-breadcrumb-item">Configuración</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Gestión de Categorías */}
                <Col xl={8}>
                    <Card className="mc-card">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5>Categorías y Subcategorías</h5>
                            <Button 
                                variant="primary" 
                                size="sm"
                                onClick={() => abrirModal('categoria')}
                            >
                                <i className="material-icons me-1">add</i>
                                Nueva Categoría
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            {categorias.map((categoria) => (
                                <div key={categoria.cat_id} className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3 p-3 border rounded" 
                                         style={{ borderLeft: `4px solid ${categoria.cat_color}` }}>
                                        <div>
                                            <h6 className="mb-1">{categoria.cat_nombre}</h6>
                                            <small className="text-muted">{categoria.cat_descripcion}</small>
                                        </div>
                                        <div>
                                            <Badge bg={categoria.cat_activo ? "success" : "danger"} className="me-2">
                                                {categoria.cat_activo ? "Activo" : "Inactivo"}
                                            </Badge>
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm" 
                                                className="me-2"
                                                onClick={() => abrirModal('subcategoria', categoria.cat_id)}
                                            >
                                                <i className="material-icons">add</i>
                                            </Button>
                                            <Button 
                                                variant="outline-secondary" 
                                                size="sm"
                                                onClick={() => toggleEstado(categoria, 'categoria')}
                                            >
                                                <i className="material-icons">
                                                    {categoria.cat_activo ? 'toggle_on' : 'toggle_off'}
                                                </i>
                                            </Button>
                                        </div>
                                    </div>
                                    
                                    <Row className="ms-3">
                                        {getSubcategoriasPorCategoria(categoria.cat_id).map((subcategoria) => (
                                            <Col key={subcategoria.sub_id} md={3} className="mb-2">
                                                <div className="d-flex justify-content-between align-items-center p-2 border rounded bg-light">
                                                    <span className={subcategoria.sub_activo ? "" : "text-muted text-decoration-line-through"}>
                                                        {subcategoria.sub_nombre}
                                                    </span>
                                                    <Button 
                                                        variant="link" 
                                                        size="sm" 
                                                        className="p-0"
                                                        onClick={() => toggleEstado(subcategoria, 'subcategoria')}
                                                    >
                                                        <i className="material-icons" style={{ fontSize: '16px' }}>
                                                            {subcategoria.sub_activo ? 'toggle_on' : 'toggle_off'}
                                                        </i>
                                                    </Button>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Gestión de Fondos */}
                <Col xl={4}>
                    <Card className="mc-card">
                        <Card.Header>
                            <h5>Fondos Presupuestarios</h5>
                        </Card.Header>
                        <Card.Body>
                            {fondos.map((fondo) => (
                                <div key={fondo.fon_id} className="mb-3 p-3 border rounded">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="mb-0">{fondo.fon_nombre}</h6>
                                        <Badge bg={fondo.fon_activo ? "success" : "danger"}>
                                            {fondo.fon_activo ? "Activo" : "Inactivo"}
                                        </Badge>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Presupuesto:</span>
                                        <strong>{formatCurrency(fondo.fon_presupuesto_anual)}</strong>
                                    </div>
                                    <div className="d-flex justify-content-between mt-2">
                                        <Button variant="outline-primary" size="sm">
                                            Editar
                                        </Button>
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => {
                                                setFondos(prev => prev.map(f => 
                                                    f.fon_id === fondo.fon_id 
                                                        ? { ...f, fon_activo: !f.fon_activo }
                                                        : f
                                                ));
                                            }}
                                        >
                                            {fondo.fon_activo ? 'Desactivar' : 'Activar'}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </Col>

              
            </Row>

            {/* Modal para agregar categoría/subcategoría */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalType === 'categoria' ? 'Nueva Categoría' : 'Nueva Subcategoría'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                name="nombre"
                                value={formData.nombre}
                                onChange={handleInputChange}
                                placeholder="Nombre de la categoría/subcategoría"
                                required
                            />
                        </Form.Group>
                        
                        {modalType === 'categoria' && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Descripción</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="descripcion"
                                        value={formData.descripcion}
                                        onChange={handleInputChange}
                                        placeholder="Descripción de la categoría"
                                    />
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>Color</Form.Label>
                                    <Form.Control
                                        type="color"
                                        name="color"
                                        value={formData.color}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </>
                        )}
                        
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                name="activo"
                                checked={formData.activo}
                                onChange={handleInputChange}
                                label="Activo"
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={guardarConfiguracion}>
                        Guardar
                    </Button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}
